import DayJS from '../../../helpers/DayJS'

/**
 * Calculates the dynamic "from" date based on selected cohorts with smart logic
 * @param selectedCohorts - Array of selected cohort dates
 * @param allCohorts - All available cohorts
 * @param currentFromDate - Current from date (if manually set)
 * @returns Date string in YYYY-MM-DD format
 */
export const calculateDynamicFromDate = (
    selectedCohorts?: string[],
    allCohorts?: string[],
    currentFromDate?: string
): string => {
    const today = DayJS().startOf('day')
    const cohortsToProcess = selectedCohorts && selectedCohorts.length > 0 ? selectedCohorts : allCohorts || []

    if (cohortsToProcess.length > 0) {
        // Determine the format of cohorts by checking the first one
        const firstCohort = cohortsToProcess[0]

        // Safety check: ensure firstCohort is a valid string
        if (!firstCohort || typeof firstCohort !== 'string') {
            return today.subtract(6, 'month').format('YYYY-MM-DD')
        }

        const isApiFormat = firstCohort.includes('-') && firstCohort.indexOf('-') === 4
        const cohortFormat = isApiFormat ? 'YYYY-MM-DD' : 'DD-MM-YYYY'

        // Filter cohorts that are before today
        const validCohorts = cohortsToProcess.filter((cohort: string) => {
            const cohortDate = DayJS(cohort, cohortFormat)
            const isValid = cohortDate.isValid()
            const isBefore = cohortDate.isBefore(today, 'day')

            return isValid && isBefore
        })

        if (validCohorts.length > 0) {
            // Sort cohorts by date (most recent first)
            const sortedCohorts = validCohorts.sort((a: string, b: string) => {
                const dateA = DayJS(a, cohortFormat)
                const dateB = DayJS(b, cohortFormat)
                return dateA.isAfter(dateB) ? -1 : 1 // Descending order
            })

            // Smart logic: Check if current from date should be preserved
            if (currentFromDate) {
                const currentFromDateObj = DayJS(currentFromDate)

                // Find cohorts that are >= current from date
                const cohortsAfterFromDate = sortedCohorts.filter((cohort) => {
                    const cohortDate = DayJS(cohort, cohortFormat)
                    return cohortDate.isSameOrAfter(currentFromDateObj, 'day')
                })

                // If current from date includes valid cohorts, keep it
                if (cohortsAfterFromDate.length > 0) {
                    return currentFromDate
                }
            }

            // Default: Use most recent cohort
            const mostRecentCohort = sortedCohorts[0]
            const result = DayJS(mostRecentCohort, cohortFormat).format('YYYY-MM-DD')

            return result
        }
    }

    // Fallback: 7 days ago
    const fallback = today.subtract(7, 'days').format('YYYY-MM-DD')
    return fallback
}

/**
 * Calculates predefined period dates for quick filter buttons
 * @param period - 0: Today, 1: Yesterday, 2: Last 7 days
 * @param dynamicFromDate - The calculated dynamic from date to use as base
 * @returns Object with from and to dates in YYYY-MM-DD format
 */
export const calculatePredefinedPeriod = (period: number, dynamicFromDate: string): { from: string; to: string } => {
    const today = DayJS()

    switch (period) {
        case 0: { // Today - but respect dynamic from if it's more recent
            const todayStr = today.format('YYYY-MM-DD')
            const fromToUse = DayJS(dynamicFromDate).isAfter(todayStr) ? dynamicFromDate : todayStr
            return {
                from: fromToUse,
                to: todayStr,
            }
        }
        case 1: { // Yesterday - but respect dynamic from if it's more recent
            const yesterday = today.subtract(1, 'day')
            const yesterdayStr = yesterday.format('YYYY-MM-DD')
            const fromToUseYesterday = DayJS(dynamicFromDate).isAfter(yesterdayStr) ? dynamicFromDate : yesterdayStr
            return {
                from: fromToUseYesterday,
                to: yesterdayStr,
            }
        }
        case 2: { // Last 7 days - calculate actual 7 days ago but respect dynamic from if it's more recent
            const sevenDaysAgo = today.subtract(7, 'days')
            const sevenDaysAgoStr = sevenDaysAgo.format('YYYY-MM-DD')
            const fromToUseLast7Days = DayJS(dynamicFromDate).isAfter(sevenDaysAgoStr)
                ? dynamicFromDate
                : sevenDaysAgoStr
            return {
                from: fromToUseLast7Days,
                to: today.format('YYYY-MM-DD'),
            }
        }
        default: {
            return {
                from: dynamicFromDate,
                to: today.format('YYYY-MM-DD'),
            }
        }
    }
}
